import React, { useEffect, useRef } from "react";
import styled from "styled-components";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import bs1 from "../../assets/bs1.jpg";
import bs2 from "../../assets/bs2.jpg";
import bs3 from "../../assets/bs3.jpg";
import bs4 from "../../assets/bs4.jpg";
import bs5 from "../../assets/bs5.jpg";
import bs6 from "../../assets/bs6.jpg";
import bs7 from "../../assets/bs7.jpg";

// Mock assets - replace with your photo paths
const photos = [
  {
    id: 1,
    url: bs1,
    title: "LENS",
    speed: 0.1,
  },
  {
    id: 2,
    url: bs2,
    title: "FOCUS",
    speed: 0.2,
  },
  {
    id: 3,
    url: bs3,
    title: "NATURE",
    speed: 0.15,
  },
  {
    id: 4,
    url: bs4,
    title: "DEPTH",
    speed: 0.25,
  },
];

const PortfolioSection = () => {
  const scrollRef = useRef(null);
  const triggerRef = useRef(null);

  useEffect(() => {
    let ctx = gsap.context(() => {
      const horizontalLength = scrollRef.current.scrollWidth;
      const viewportWidth = window.innerWidth;

      // Horizontal Scroll Animation
      gsap.to(scrollRef.current, {
        x: -(horizontalLength - viewportWidth),
        ease: "none",
        scrollTrigger: {
          trigger: triggerRef.current,
          start: "top top",
          end: () => `+=${horizontalLength}`,
          pin: true,
          scrub: 1,
          invalidateOnRefresh: true,
        },
      });

      // Individual Image Parallax & Tilt
      photos.forEach((_, i) => {
        gsap.fromTo(
          `.img-card-${i}`,
          { rotation: 5, scale: 0.9 },
          {
            rotation: -5,
            scale: 1,
            scrollTrigger: {
              trigger: `.img-card-${i}`,
              containerAnimation: gsap.getById("mainHorizontal"), // Sync with horizontal scroll
              start: "left right",
              end: "right left",
              scrub: true,
            },
          },
        );
      });
    }, triggerRef);

    return () => ctx.revert();
  }, []);

  return (
    <HorizontalWrapper ref={triggerRef}>
      <BackgroundText>WORKS</BackgroundText>

      <ScrollContent ref={scrollRef}>
        <IntroBlock>
          <span>SELECTED</span>
          <h2>WORKS</h2>
          <p>© 2026 ARCHIVE</p>
        </IntroBlock>

        {photos.map((photo, index) => (
          <ImageCard key={photo.id} className={`img-card-${index}`}>
            <div className="inner">
              <img src={photo.url} alt={photo.title} />
              <CardTitle>{photo.title}</CardTitle>
            </div>
          </ImageCard>
        ))}

        <OutroBlock>
          <h2>VIEW ALL</h2>
        </OutroBlock>
      </ScrollContent>
    </HorizontalWrapper>
  );
};

export default PortfolioSection;

// --- Styled Components ---

const HorizontalWrapper = styled.section`
  overflow: hidden;
  background: #080808;
  position: relative;
`;
const BackgroundText = styled.h1`
  position: absolute;
  top: 50%;
  left: 0%;
  transform: translateY(-50%);
  font-size: 25vw;
  font-weight: 900;
  color: rgba(255, 255, 255, 0.03);
  white-space: nowrap;
  pointer-events: none;
  z-index: 0;
`;

const ScrollContent = styled.div`
  display: flex;
  height: 100vh;
  width: fit-content;
  align-items: center;
  padding: 0 10vw;
  position: relative;
  z-index: 1;
`;

const IntroBlock = styled.div`
  margin-right: 20vw;
  span {
    font-size: 1.2rem;
    letter-spacing: 0.4em;
    color: #666;
  }
  h2 {
    font-size: 8vw;
    line-height: 0.8;
    margin: 0;
  }
  p {
    font-family: monospace;
    margin-top: 20px;
    opacity: 0.5;
  }
`;

const ImageCard = styled.div`
  width: 35vw;
  height: auto;
  margin-right: 15vw;
  flex-shrink: 0;

  .inner {
    width: 100%;
    height: 100%;
    position: relative;
    overflow: hidden;
    transition: transform 0.6s cubic-bezier(0.16, 1, 0.3, 1);

    &:hover {
      transform: scale(1.05);
      img {
        transform: scale(1.1);
      }
    }
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    filter: grayscale(20%);
    transition: transform 1.2s cubic-bezier(0.16, 1, 0.3, 1);
  }
`;

const CardTitle = styled.span`
  position: absolute;
  bottom: 20px;
  left: 20px;
  font-size: 0.8rem;
  letter-spacing: 0.3em;
  background: rgba(0, 0, 0, 0.5);
  padding: 5px 10px;
  backdrop-filter: blur(5px);
`;

const OutroBlock = styled.div`
  padding-right: 20vw;
  h2 {
    font-size: 5vw;
    border-bottom: 2px solid white;
    cursor: pointer;
    transition: opacity 0.3s;
    &:hover {
      opacity: 0.5;
    }
  }
`;
